import { MousePointer2, XIcon } from "lucide-react";

import { AiChatComposer } from "~/ai-chat/feature/ai-chat-composer";
import { AiChatTranscript } from "~/ai-chat/feature/ai-chat-transcript";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useAiChatWindowSize } from "~/ai-chat/feature/use-ai-chat-window-size";
import { useChatSend } from "~/ai-chat/feature/use-chat-send";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/utils/cn";

export function AiChatFloatingWidget() {
  const { isOpen, isAvailable, open, close } = useAiChat();

  // Once a chat call returns 404 ("AI not configured on this server"), the
  // launcher hides for the rest of the session. If the user already had the
  // window open when that happened, keep it open so they can see the inline
  // error in the transcript.
  if (!isAvailable && !isOpen) return null;

  return isOpen ? <AiChatWindow onClose={close} /> : <AiChatLauncher onOpen={open} />;
}

function AiChatLauncher({ onOpen }: { onOpen: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={onOpen}
          aria-label="Open Istari AI"
          className={cn(
            "fixed right-4 bottom-4 z-50 size-12 rounded-full p-0 shadow-lg sm:right-6 sm:bottom-6",
            "transition-shadow duration-150 hover:shadow-xl",
          )}
        >
          <MousePointer2 className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">Chat with Istari</TooltipContent>
    </Tooltip>
  );
}

function AiChatWindow({ onClose }: { onClose: () => void }) {
  const { send, isPending, activity } = useChatSend();
  const { size, isResizing, resizeHandleProps } = useAiChatWindowSize();

  return (
    <aside
      aria-label="Istari Chat"
      style={{ width: size.width, height: size.height }}
      className={cn(
        "border-border bg-background fixed z-50 flex flex-col rounded-xl border shadow-2xl",
        "right-4 bottom-4 sm:right-6 sm:bottom-6",
        "max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)]",
        "sm:max-h-[calc(100vh-3rem)] sm:max-w-[calc(100vw-3rem)]",
        "animate-in fade-in slide-in-from-bottom-2 zoom-in-95 duration-150",
        isResizing && "select-none",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            {...resizeHandleProps}
            role="separator"
            aria-label="Resize chat window"
            tabIndex={0}
            className={cn(
              "absolute -top-1 -left-1 z-10 size-5 cursor-nwse-resize touch-none rounded-tl-xl",
              "focus-visible:ring-ring outline-none focus-visible:ring-2",
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="left">Drag to resize, double-click to reset</TooltipContent>
      </Tooltip>
      <header className="border-border flex shrink-0 items-center justify-between gap-2 border-b-[0.5px] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <MousePointer2 className="text-muted-foreground size-4 shrink-0" />
          <span className="truncate text-sm font-medium">New chat</span>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="xs" className="px-1.5" onClick={onClose} aria-label="Close Istari AI">
              <XIcon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Close</TooltipContent>
        </Tooltip>
      </header>
      <AiChatTranscript
        isPending={isPending}
        activity={activity}
        onSuggestionSelect={(suggestion) => void send(suggestion)}
      />
      <AiChatComposer onSend={(content) => void send(content)} isPending={isPending} />
    </aside>
  );
}
